import { useState } from "react"
import { faChevronDown, faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ButtonBase, Collapse, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { HIDDEN_IN_PRINT } from "../../../../../shared/components/print/printPageOnly"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"

import { formatHourRange, formatTimeWindow, getPeakHour } from "../meetingRoomsLib"
import type { HourOccupancy } from "../meetingRoomsTypes"
import { loadOccupancyChartOpen, saveOccupancyChartOpen } from "./helpers"
import { OccupancyHourBar } from "./OccupancyHourBar"

const useStyles = makeStyles()((theme) => ({
    panel: {
        padding: "1rem 1.5rem 1.25rem",
        borderRadius: "1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        backgroundColor: theme.palette.tailwind.slate[50],
        "@media print": {
            marginTop: "1.25rem",
            breakInside: "avoid",
        },
    },
    panelCollapsed: HIDDEN_IN_PRINT,
    header: {
        flexWrap: "wrap",
    },
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    peak: {
        padding: ".25rem .75rem",
        borderRadius: "6.25rem",
        backgroundColor: theme.palette.tailwind.blue[50],
        color: theme.palette.primary.main,
        transition: theme.transitions.create("background-color", {
            duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
            backgroundColor: theme.palette.tailwind.blue[100],
        },
    },
    peakText: {
        fontWeight: 600,
    },
    peakLink: {
        marginLeft: ".25rem",
        fontWeight: 700,
        textDecoration: "underline",
        ...HIDDEN_IN_PRINT,
    },
    peakIcon: {
        fontSize: ".75rem",
        ...HIDDEN_IN_PRINT,
    },
    actions: {
        marginLeft: "auto",
        ...HIDDEN_IN_PRINT,
    },
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
    toggle: {
        padding: ".25rem .75rem",
        borderRadius: "6.25rem",
        color: theme.palette.tailwind.slate[500],
    },
    toggleText: {
        fontWeight: 700,
    },
    chevron: {
        fontSize: ".875rem",
        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
    },
    chevronOpen: {
        transform: "rotate(180deg)",
    },
    chart: {
        marginTop: "1rem",
    },
    axis: {
        height: "7rem",
        marginTop: ".75rem",
        flexShrink: 0,
        width: "2rem",
        color: theme.palette.tailwind.slate[500],
    },
    axisLabel: {
        fontWeight: 600,
        transform: "translateY(-50%)",
        "&:last-of-type": {
            transform: "translateY(50%)",
        },
    },
    scroller: {
        width: 0,
        flex: 1,
        overflowX: "auto",
        "@media print": {
            width: "auto",
            overflow: "visible",
        },
    },
    plot: {
        position: "relative",
        display: "grid",
        gridAutoColumns: "minmax(3.5rem, 1fr)",
        gridAutoFlow: "column",
        width: "max-content",
        minWidth: "100%",
        "@media print": {
            gridAutoColumns: "minmax(0, 1fr)",
            width: "100%",
        },
    },
    gridLines: {
        position: "absolute",
        top: ".75rem",
        left: 0,
        right: 0,
        height: "7rem",
        borderTop: `1px dashed ${theme.palette.tailwind.slate[200]}`,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        pointerEvents: "none",
        "&::after": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            borderTop: `1px dashed ${theme.palette.tailwind.slate[200]}`,
        },
    },
    column: {
        position: "relative",
        paddingTop: ".75rem",
        paddingBottom: ".5rem",
        borderRadius: ".75rem",
        transition: theme.transitions.create("background-color", {
            duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[100],
        },
    },
    columnSelected: {
        backgroundColor: theme.palette.tailwind.blue[50],
        "&:hover": {
            backgroundColor: theme.palette.tailwind.blue[100],
        },
    },
    columnPlot: {
        height: "7rem",
    },
    count: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[500],
    },
    countCurrent: {
        color: theme.palette.tailwind.teal[700],
    },
    countSelected: {
        color: theme.palette.tailwind.slate[900],
    },
    nowBadge: {
        padding: "0 .375rem",
        borderRadius: "6.25rem",
        fontWeight: 700,
        textTransform: "uppercase",
        backgroundColor: theme.palette.tailwind.teal[700],
        color: theme.palette.common.white,
    },
    hourLabel: {
        marginTop: ".5rem",
        fontWeight: 500,
        color: theme.palette.tailwind.slate[600],
    },
    hourLabelCurrent: {
        fontWeight: 800,
    },
    hourLabelSelected: {
        fontWeight: 800,
        color: theme.palette.tailwind.slate[900],
    },
}))

type Props = Readonly<{
    hours: ReadonlyArray<HourOccupancy>
    totalRooms: number
    selectedHour: number
    currentHour?: number
    onHourSelect: (hour: number) => void
    onPeakHourSelect: (hour: number) => void
}>

export function OccupancyByHour({
    hours,
    totalRooms,
    selectedHour,
    currentHour,
    onHourSelect,
    onPeakHourSelect,
}: Props) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const [isOpen, setIsOpen] = useState(loadOccupancyChartOpen)

    const peak = getPeakHour(hours)
    const axisValues = [totalRooms, Math.round(totalRooms / 2), 0]

    const toggleChart = () => {
        saveOccupancyChartOpen(!isOpen)
        setIsOpen(!isOpen)
    }

    const selectPeakHour = (hour: number) => {
        if (!isOpen) toggleChart()
        onPeakHourSelect(hour)
    }

    return (
        <Stack className={cx(classes.panel, !isOpen && classes.panelCollapsed)}>
            <Stack direction="row" alignItems="center" spacing="1rem" useFlexGap className={classes.header}>
                <LocalizedStrict
                    id={
                        currentHour === undefined
                            ? "service-center-meeting-rooms-occupancy-by-day-title"
                            : "service-center-meeting-rooms-occupancy-by-hour-title"
                    }
                >
                    <Typography variant="subtitle2" className={classes.title}>
                        Occupancy today
                    </Typography>
                </LocalizedStrict>
                {peak && peak.bookedCount > 0 && (
                    <LocalizedStrict
                        id="service-center-meeting-rooms-occupancy-peak-tooltip"
                        vars={{ hour: formatTimeWindow(dateTimeFactory, peak.window) }}
                        attrs={{ title: true }}
                    >
                        <Tooltip title="">
                            <Stack
                                component={ButtonBase}
                                direction="row"
                                alignItems="center"
                                spacing=".5rem"
                                className={classes.peak}
                                onClick={() => selectPeakHour(peak.hour)}
                            >
                                <LocalizedStrict
                                    id="service-center-meeting-rooms-occupancy-peak-button"
                                    vars={{
                                        hour: formatTimeWindow(dateTimeFactory, peak.window),
                                        peak: peak.bookedCount,
                                        total: totalRooms,
                                    }}
                                    elems={{ action: <span className={classes.peakLink} /> }}
                                >
                                    <Typography variant="body2" component="span" className={classes.peakText}>
                                        Busiest hour
                                    </Typography>
                                </LocalizedStrict>
                                <FontAwesomeIcon icon={faChevronRight} className={classes.peakIcon} />
                            </Stack>
                        </Tooltip>
                    </LocalizedStrict>
                )}
                <Stack direction="row" alignItems="center" spacing="1rem" className={classes.actions}>
                    <LocalizedStrict id="service-center-meeting-rooms-occupancy-hint">
                        <Typography variant="body2" className={classes.hint}>
                            Click an hour to filter
                        </Typography>
                    </LocalizedStrict>
                    <Stack
                        component={ButtonBase}
                        direction="row"
                        alignItems="center"
                        spacing=".375rem"
                        aria-expanded={isOpen}
                        className={classes.toggle}
                        onClick={toggleChart}
                    >
                        <LocalizedStrict
                            id={
                                isOpen
                                    ? "service-center-meeting-rooms-occupancy-hide-chart-button"
                                    : "service-center-meeting-rooms-occupancy-show-chart-button"
                            }
                        >
                            <Typography variant="body2" component="span" className={classes.toggleText}>
                                {isOpen ? "Hide chart" : "Show chart"}
                            </Typography>
                        </LocalizedStrict>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={cx(classes.chevron, isOpen && classes.chevronOpen)}
                        />
                    </Stack>
                </Stack>
            </Stack>
            <Collapse in={isOpen}>
                <Stack direction="row" className={classes.chart}>
                    <Stack justifyContent="space-between" className={classes.axis}>
                        {axisValues.map((value, index) => (
                            <Typography key={index} variant="caption" className={classes.axisLabel}>
                                {value}
                            </Typography>
                        ))}
                    </Stack>
                    <div className={classes.scroller}>
                        <div className={classes.plot}>
                            <div className={classes.gridLines} />
                            {hours.map(({ hour, window, bookedCount }) => {
                                const isSelected = hour === selectedHour
                                const isCurrent = hour === currentHour
                                const jumpsToNow = isSelected && currentHour !== undefined && !isCurrent

                                return (
                                    <LocalizedStrict
                                        key={hour}
                                        id={
                                            jumpsToNow
                                                ? "service-center-meeting-rooms-occupancy-hour-tooltip-jump-to-now"
                                                : "service-center-meeting-rooms-occupancy-hour-tooltip"
                                        }
                                        vars={{
                                            hour: formatTimeWindow(dateTimeFactory, window),
                                            booked: bookedCount,
                                            total: totalRooms,
                                        }}
                                        attrs={{ title: true }}
                                    >
                                        <Tooltip title="">
                                            <Stack
                                                component={ButtonBase}
                                                aria-pressed={isSelected}
                                                alignItems="stretch"
                                                className={cx(classes.column, isSelected && classes.columnSelected)}
                                                onClick={() =>
                                                    onHourSelect(
                                                        isSelected && currentHour !== undefined ? currentHour : hour,
                                                    )
                                                }
                                            >
                                                <Stack
                                                    alignItems="center"
                                                    justifyContent="flex-end"
                                                    spacing=".125rem"
                                                    className={classes.columnPlot}
                                                >
                                                    {isCurrent && (
                                                        <LocalizedStrict id="service-center-meeting-rooms-occupancy-now-badge">
                                                            <Typography variant="caption" className={classes.nowBadge}>
                                                                Now
                                                            </Typography>
                                                        </LocalizedStrict>
                                                    )}
                                                    {bookedCount > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            className={cx(
                                                                classes.count,
                                                                isCurrent && classes.countCurrent,
                                                                isSelected && classes.countSelected,
                                                            )}
                                                        >
                                                            {bookedCount}
                                                        </Typography>
                                                    )}
                                                    <OccupancyHourBar
                                                        heightPercent={
                                                            totalRooms ? (bookedCount / totalRooms) * 100 : 0
                                                        }
                                                        isCurrent={isCurrent}
                                                        isSelected={isSelected}
                                                        isEmpty={bookedCount === 0}
                                                    />
                                                </Stack>
                                                <Typography
                                                    variant="caption"
                                                    className={cx(
                                                        classes.hourLabel,
                                                        isCurrent && classes.hourLabelCurrent,
                                                        isSelected && classes.hourLabelSelected,
                                                    )}
                                                >
                                                    {formatHourRange(dateTimeFactory, window)}
                                                </Typography>
                                            </Stack>
                                        </Tooltip>
                                    </LocalizedStrict>
                                )
                            })}
                        </div>
                    </div>
                </Stack>
            </Collapse>
        </Stack>
    )
}
